import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Play, Pause, X, Volume2, VolumeX, ArrowRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  exterior: "Exterior",
  interior: "Interior · 45 m²",
  cardio: "Zona Cardio",
  equipamento: "Equipamento",
  geral: "Estúdio",
};

const ORDER = ["exterior", "interior", "cardio", "equipamento", "geral"];
const SECONDS_PER_SLIDE = 5; // duração de cada foto

function orderPhotos(photos) {
  const groups = ORDER.map((cat) => ({
    cat,
    items: photos.filter((p) => (p.categoria || "geral") === cat),
  })).filter((g) => g.items.length > 0);
  return groups.flatMap((g) => g.items.map((p) => ({ ...p, _cat: g.cat })));
}

export default function VirtualTour({ onClose }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/studio/photos`)
      .then((res) => {
        if (cancelled) return;
        setPhotos(orderPhotos(res.data || []));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!playing || photos.length === 0) return undefined;
    const start = Date.now();
    const totalMs = SECONDS_PER_SLIDE * 1000;
    tickRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.min(1, elapsed / totalMs));
    }, 50);
    timerRef.current = setTimeout(() => {
      setIdx((i) => (i + 1) % photos.length);
      setProgress(0);
    }, totalMs);
    return () => {
      clearTimeout(timerRef.current);
      clearInterval(tickRef.current);
    };
  }, [idx, playing, photos.length]);

  // Atalhos de teclado
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === " ") {
        e.preventDefault();
        setPlaying((p) => !p);
      }
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % Math.max(photos.length, 1));
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + photos.length) % Math.max(photos.length, 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photos.length, onClose]);

  const current = photos[idx];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black"
      data-testid="virtual-tour"
    >
      {/* Slides */}
      {photos.map((p, i) => (
        <div
          key={p.id}
          className="absolute inset-0 transition-opacity duration-[1200ms] ease-in-out"
          style={{
            opacity: i === idx ? 1 : 0,
            pointerEvents: i === idx ? "auto" : "none",
          }}
        >
          <img
            src={p.foto}
            alt={p.titulo || CATEGORY_LABELS[p._cat] || "Estúdio"}
            className="w-full h-full object-cover"
            style={{
              animation: i === idx && playing ? `kenburns-${(i % 4) + 1} ${SECONDS_PER_SLIDE + 2}s ease-in-out forwards` : "none",
            }}
          />
          {/* Vinheta + overlay escuro */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/40" />
        </div>
      ))}

      {/* Estados */}
      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center text-neutral-400">
          <Loader2 className="animate-spin mr-2" /> A preparar tour...
        </div>
      ) : photos.length === 0 ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <div className="text-neutral-400 mb-3">Ainda não há fotos do estúdio.</div>
          <button onClick={onClose} className="btn-primary" data-testid="virtual-tour-close-empty">
            Fechar
          </button>
        </div>
      ) : (
        <>
          {/* HUD topo */}
          <div className="absolute top-0 left-0 right-0 px-6 md:px-12 py-5 flex items-center justify-between gap-4 z-10">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-1">
                Tour Virtual · O-MEU-PT.PT
              </div>
              <div className="font-display text-2xl md:text-4xl uppercase tracking-tight leading-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">
                {current?.titulo || CATEGORY_LABELS[current?._cat] || "Estúdio"}
              </div>
              {current?._cat && current?.titulo ? (
                <div className="text-xs text-neutral-400 uppercase tracking-widest mt-1">
                  {CATEGORY_LABELS[current._cat]}
                </div>
              ) : null}
            </div>
            <button
              onClick={onClose}
              className="w-12 h-12 border border-neutral-700 bg-black/50 backdrop-blur-sm flex items-center justify-center text-neutral-200 hover:text-yellow-400 hover:border-yellow-400/60 transition-colors"
              data-testid="virtual-tour-close"
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
          </div>

          {/* Progress bar de slides */}
          <div className="absolute top-0 left-0 right-0 flex gap-1 px-6 md:px-12 pt-2 z-10">
            {photos.map((_, i) => (
              <div key={i} className="flex-1 h-0.5 bg-white/15 overflow-hidden">
                <div
                  className="h-full bg-yellow-400 transition-[width] duration-100"
                  style={{
                    width: i < idx ? "100%" : i === idx ? `${progress * 100}%` : "0%",
                  }}
                />
              </div>
            ))}
          </div>

          {/* HUD fundo */}
          <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 py-5 flex items-center justify-between gap-4 z-10">
            <button
              onClick={() => setPlaying((p) => !p)}
              className="w-12 h-12 rounded-full bg-yellow-400 text-black flex items-center justify-center hover:scale-105 transition-transform"
              data-testid="virtual-tour-playpause"
              aria-label={playing ? "Pausar" : "Reproduzir"}
            >
              {playing ? <Pause size={18} /> : <Play size={18} />}
            </button>

            <div className="text-xs text-neutral-500 uppercase tracking-widest">
              {idx + 1} / {photos.length}
            </div>

            <button
              onClick={() => setIdx((i) => (i + 1) % photos.length)}
              className="btn-ghost text-xs inline-flex items-center gap-2 bg-black/40 backdrop-blur-sm"
              data-testid="virtual-tour-next"
            >
              Próximo <ArrowRight size={14} />
            </button>
          </div>
        </>
      )}

      {/* Keyframes Ken Burns */}
      <style>{`
        @keyframes kenburns-1 {
          0% { transform: scale(1) translate(0,0); }
          100% { transform: scale(1.12) translate(-2%, -1%); }
        }
        @keyframes kenburns-2 {
          0% { transform: scale(1.12) translate(2%, 1%); }
          100% { transform: scale(1) translate(0,0); }
        }
        @keyframes kenburns-3 {
          0% { transform: scale(1) translate(0,0); }
          100% { transform: scale(1.10) translate(1%, -2%); }
        }
        @keyframes kenburns-4 {
          0% { transform: scale(1.08) translate(-1%, 2%); }
          100% { transform: scale(1) translate(0,0); }
        }
      `}</style>
    </div>
  );
}
